import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";

const BowlingGame = ({ API, token, onClose }) => {
  const { user } = useContext(AuthContext);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Game state
  const [gamePhase, setGamePhase] = useState('aiming');
  const [frame, setFrame] = useState(1);
  const [roll, setRoll] = useState(1);
  const [scores, setScores] = useState(Array(10).fill(null).map(() => ({ roll1: null, roll2: null, roll3: null, total: null })));
  const [totalScore, setTotalScore] = useState(0);
  const [pins, setPins] = useState(Array(10).fill(true));
  const [ballX, setBallX] = useState(150); // Ball horizontal position (50-250)
  const [ballY, setBallY] = useState(380);
  const [ballVelocity, setBallVelocity] = useState({ x: 0, y: 0 });
  const [aimAngle, setAimAngle] = useState(0);
  const [power, setPower] = useState(70);
  const [spin, setSpin] = useState(0);
  const [lastResult, setLastResult] = useState('');
  const [chips, setChips] = useState(0);
  
  const LANE_WIDTH = 300;
  const LANE_HEIGHT = 420;
  const PIN_RADIUS = 7;
  const BALL_RADIUS = 10;
  
  const PIN_POSITIONS = [
    { x: 150, y: 70 },
    { x: 130, y: 50 },
    { x: 170, y: 50 },
    { x: 110, y: 30 },
    { x: 150, y: 30 },
    { x: 190, y: 30 },
    { x: 90, y: 10 },
    { x: 130, y: 10 },
    { x: 170, y: 10 },
    { x: 210, y: 10 },
  ];

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await axios.get(`${API}/fchips/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChips(res.data.balance || 0);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    fetchBalance();
  }, [API, token]);

  const resetPins = useCallback(() => {
    setPins(Array(10).fill(true));
  }, []);

  const calculateKnockedPins = useCallback((bx, by, currentPins) => {
    const knocked = [...currentPins];
    let count = 0;
    
    PIN_POSITIONS.forEach((pin, i) => {
      if (knocked[i]) {
        const dx = bx - pin.x;
        const dy = by - pin.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < BALL_RADIUS + PIN_RADIUS + 5) {
          const knockChance = Math.max(0.3, 1 - (distance / (BALL_RADIUS + PIN_RADIUS + 20)));
          if (Math.random() < knockChance * (power / 50)) {
            knocked[i] = false;
            count++;
            
            PIN_POSITIONS.forEach((otherPin, j) => {
              if (i !== j && knocked[j]) {
                const odx = pin.x - otherPin.x;
                const ody = pin.y - otherPin.y;
                const odist = Math.sqrt(odx * odx + ody * ody);
                if (odist < PIN_RADIUS * 4 && Math.random() < 0.4) {
                  knocked[j] = false;
                  count++;
                }
              }
            });
          }
        }
      }
    });
    
    return { knocked, count };
  }, [power]);

  const throwBall = () => {
    if (gamePhase !== 'aiming') return;
    
    setGamePhase('rolling');
    const angleRad = (aimAngle * Math.PI) / 180;
    const speed = power / 8;
    
    setBallVelocity({
      x: Math.sin(angleRad) * speed + spin * 0.3,
      y: -speed
    });
  };

  useEffect(() => {
    if (gamePhase !== 'rolling') return;
    
    let currentBallX = ballX;
    let currentBallY = ballY;
    let vx = ballVelocity.x;
    let vy = ballVelocity.y;
    let currentPins = [...pins];
    
    const animate = () => {
      vx += spin * 0.02;
      currentBallX += vx;
      currentBallY += vy;
      
      if (currentBallX < BALL_RADIUS) {
        currentBallX = BALL_RADIUS;
        vx = -vx * 0.5;
      }
      if (currentBallX > LANE_WIDTH - BALL_RADIUS) {
        currentBallX = LANE_WIDTH - BALL_RADIUS;
        vx = -vx * 0.5;
      }
      
      const { knocked, count } = calculateKnockedPins(currentBallX, currentBallY, currentPins);
      if (count > 0) {
        currentPins = knocked;
        setPins(knocked);
      }
      
      setBallX(currentBallX);
      setBallY(currentBallY);
      
      if (currentBallY < -20 || currentBallY > LANE_HEIGHT + 20) {
        finishRoll(currentPins);
        return;
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gamePhase, ballVelocity, spin, calculateKnockedPins, pins]);

  const finishRoll = (finalPins) => {
    const knockedCount = finalPins.filter(p => !p).length;
    const pinsDown = roll === 1 ? knockedCount : knockedCount - (10 - pins.filter(p => p).length);
    
    const newScores = [...scores];
    const currentFrame = newScores[frame - 1];
    
    if (roll === 1) {
      currentFrame.roll1 = pinsDown;
      if (pinsDown === 10) {
        setLastResult('🎳 STRIKE! 🎳');
        if (frame < 10) {
          setFrame(frame + 1);
          setRoll(1);
          resetPins();
        } else {
          setRoll(2);
          resetPins();
        }
      } else {
        setRoll(2);
        setLastResult(pinsDown > 0 ? `Knocked down ${pinsDown}` : 'Gutter ball!');
      }
    } else if (roll === 2) {
      currentFrame.roll2 = pinsDown;
      if (currentFrame.roll1 + pinsDown === 10) {
        setLastResult('✨ SPARE! ✨');
      } else {
        setLastResult(pinsDown > 0 ? `Knocked down ${pinsDown}` : 'Miss');
      }
      
      if (frame < 10) {
        setFrame(frame + 1);
        setRoll(1);
        resetPins();
      } else if (currentFrame.roll1 === 10 || currentFrame.roll1 + currentFrame.roll2 === 10) {
        setRoll(3);
        resetPins();
      } else {
        calculateFinalScore(newScores);
      }
    } else {
      currentFrame.roll3 = pinsDown;
      calculateFinalScore(newScores);
    }
    
    setScores(newScores);
    setBallX(150);
    setBallY(380);
    setGamePhase('aiming');
  };

  const calculateFinalScore = (finalScores) => {
    let total = 0;
    for (let i = 0; i < 10; i++) {
      const f = finalScores[i];
      if (f.roll1 === null) continue;
      
      let frameScore = f.roll1;
      if (f.roll1 === 10) {
        const next1 = finalScores[i + 1]?.roll1 ?? f.roll2 ?? 0;
        const next2 = (finalScores[i + 1]?.roll1 === 10 ? finalScores[i + 2]?.roll1 : finalScores[i + 1]?.roll2) ?? f.roll3 ?? 0;
        frameScore = 10 + next1 + next2;
      } else if (f.roll1 + (f.roll2 ?? 0) === 10) {
        const next1 = finalScores[i + 1]?.roll1 ?? f.roll3 ?? 0;
        frameScore = 10 + next1;
      } else {
        frameScore = f.roll1 + (f.roll2 ?? 0);
      }
      
      total += frameScore;
      f.total = total;
    }
    setTotalScore(total);
    setLastResult(`🏆 Final Score: ${total}!`);
    setGamePhase('finished');
    
    if (total >= 200) {
      setChips(prev => prev + 50);
      toast.success('Amazing game! +50 F-Chips!');
    } else if (total >= 150) {
      setChips(prev => prev + 20);
      toast.success('Great game! +20 F-Chips!');
    } else if (total >= 100) {
      setChips(prev => prev + 10);
      toast.success('Good game! +10 F-Chips!');
    }
  };

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Lane background
    const laneGradient = ctx.createLinearGradient(0, 0, 0, LANE_HEIGHT);
    laneGradient.addColorStop(0, '#8B4513');
    laneGradient.addColorStop(0.3, '#CD853F');
    laneGradient.addColorStop(1, '#DEB887');
    ctx.fillStyle = laneGradient;
    ctx.fillRect(0, 0, LANE_WIDTH, LANE_HEIGHT);
    
    // Gutters
    ctx.fillStyle = '#2C2C2C';
    ctx.fillRect(0, 0, 15, LANE_HEIGHT);
    ctx.fillRect(LANE_WIDTH - 15, 0, 15, LANE_HEIGHT);
    
    // Lane arrows
    ctx.fillStyle = 'rgba(139, 69, 19, 0.5)';
    for (let i = 0; i < 7; i++) {
      const arrowX = 50 + i * 35;
      ctx.beginPath();
      ctx.moveTo(arrowX, 300);
      ctx.lineTo(arrowX - 6, 320);
      ctx.lineTo(arrowX + 6, 320);
      ctx.closePath();
      ctx.fill();
    }
    
    // Foul line
    ctx.strokeStyle = '#FF0000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, 350);
    ctx.lineTo(LANE_WIDTH, 350);
    ctx.stroke();
    
    // Pin deck
    ctx.fillStyle = '#F5F5DC';
    ctx.fillRect(70, 0, 160, 90);
    
    // Draw pins
    PIN_POSITIONS.forEach((pin, i) => {
      if (pins[i]) {
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(pin.x, pin.y + 20, PIN_RADIUS, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(pin.x, pin.y + 20, PIN_RADIUS - 2, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.beginPath();
        ctx.ellipse(pin.x + 3, pin.y + 22, PIN_RADIUS, PIN_RADIUS / 2, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw ball
    const gradient = ctx.createRadialGradient(ballX - 2, ballY - 2, 0, ballX, ballY, BALL_RADIUS);
    gradient.addColorStop(0, '#4169E1');
    gradient.addColorStop(1, '#00008B');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(ballX, ballY, BALL_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    
    // Ball shine
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.beginPath();
    ctx.arc(ballX - 2, ballY - 2, BALL_RADIUS / 3, 0, Math.PI * 2);
    ctx.fill();
    
    // Aim line
    if (gamePhase === 'aiming') {
      const angleRad = (aimAngle * Math.PI) / 180;
      const lineLength = 80 + power * 0.5;
      
      ctx.strokeStyle = 'rgba(255, 255, 0, 0.8)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(ballX, ballY);
      ctx.lineTo(ballX + Math.sin(angleRad) * lineLength, ballY - lineLength);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [pins, ballX, ballY, gamePhase, aimAngle, power]);

  const newGame = () => {
    setFrame(1);
    setRoll(1);
    setScores(Array(10).fill(null).map(() => ({ roll1: null, roll2: null, roll3: null, total: null })));
    setTotalScore(0);
    resetPins();
    setBallX(150);
    setBallY(380);
    setGamePhase('aiming');
    setLastResult('');
  };

  const moveBall = (direction) => {
    if (gamePhase !== 'aiming') return;
    setBallX(prev => Math.max(30, Math.min(LANE_WIDTH - 30, prev + direction * 15)));
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full h-full flex bg-gray-900">
      {/* Left Side - Controls */}
      <div className="w-48 bg-gradient-to-b from-gray-800 to-gray-900 p-3 flex flex-col border-r border-gray-700">
        {/* Header */}
        <div className="text-center mb-3">
          <span className="text-2xl">🎳</span>
          <div className="text-white font-bold text-sm">10-PIN BOWLING</div>
          <div className="text-yellow-400 text-xs">💰 {chips.toLocaleString()}</div>
        </div>

        {/* Scoreboard Mini */}
        <div className="bg-gray-800 rounded p-2 mb-3">
          <div className="text-[10px] text-gray-400 text-center">Frame {frame}/10 • Roll {roll}</div>
          <div className="text-center text-2xl font-bold text-yellow-400">{totalScore}</div>
        </div>

        {/* Ball Position */}
        {gamePhase === 'aiming' && (
          <div className="mb-3">
            <div className="text-gray-400 text-xs mb-1 text-center">Ball Position</div>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => moveBall(-1)}
                className="p-2 bg-blue-600 hover:bg-blue-500 rounded text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-white text-xs w-12 text-center">{Math.round((ballX / LANE_WIDTH) * 100)}%</span>
              <button
                onClick={() => moveBall(1)}
                className="p-2 bg-blue-600 hover:bg-blue-500 rounded text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Aim */}
        {gamePhase === 'aiming' && (
          <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Aim</span>
              <span className="text-yellow-400">{aimAngle}°</span>
            </div>
            <input
              type="range"
              min="-30"
              max="30"
              value={aimAngle}
              onChange={(e) => setAimAngle(parseInt(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
            />
          </div>
        )}

        {/* Power */}
        {gamePhase === 'aiming' && (
          <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Power</span>
              <span className="text-green-400">{power}%</span>
            </div>
            <input
              type="range"
              min="30"
              max="100"
              value={power}
              onChange={(e) => setPower(parseInt(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
            />
          </div>
        )}

        {/* Spin */}
        {gamePhase === 'aiming' && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Spin</span>
              <span className="text-purple-400">{spin > 0 ? `+${spin}` : spin}</span>
            </div>
            <input
              type="range"
              min="-5"
              max="5"
              value={spin}
              onChange={(e) => setSpin(parseInt(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
            />
          </div>
        )}

        {/* Throw Button */}
        {gamePhase === 'aiming' && (
          <button
            onClick={throwBall}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-lg text-white font-bold text-sm mb-2"
          >
            🎳 THROW!
          </button>
        )}

        {/* New Game */}
        {gamePhase === 'finished' && (
          <button
            onClick={newGame}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> NEW GAME
          </button>
        )}

        {/* Result */}
        {lastResult && (
          <div className="mt-auto text-center text-yellow-400 font-bold text-sm animate-pulse p-2 bg-black/30 rounded">
            {lastResult}
          </div>
        )}
      </div>

      {/* Right Side - Lane */}
      <div className="flex-1 flex flex-col">
        {/* Full Scoreboard */}
        <div className="bg-gray-800 px-2 py-1 border-b border-gray-700">
          <div className="flex gap-0.5 text-[9px] text-white">
            {scores.map((s, i) => (
              <div key={i} className={`flex-1 border border-gray-600 ${frame === i + 1 ? 'bg-yellow-900/50' : 'bg-gray-900'}`}>
                <div className="text-center border-b border-gray-600 text-gray-500">{i + 1}</div>
                <div className="flex justify-center gap-0.5 px-0.5">
                  <span>{s.roll1 === 10 ? 'X' : s.roll1 ?? '-'}</span>
                  <span>{s.roll1 === 10 ? '' : (s.roll1 + s.roll2 === 10 ? '/' : s.roll2 ?? '-')}</span>
                  {i === 9 && <span>{s.roll3 ?? ''}</span>}
                </div>
                <div className="text-center font-bold text-yellow-400 text-[10px]">{s.total ?? ''}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Lane Canvas */}
        <div className="flex-1 flex items-center justify-center bg-gray-950 p-2">
          <canvas
            ref={canvasRef}
            width={LANE_WIDTH}
            height={LANE_HEIGHT}
            className="border-4 border-amber-800 rounded shadow-2xl"
            style={{ maxHeight: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default BowlingGame;
